import { useToggle } from '@alluradirect/react';
import { Typography } from '@mui/material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import Grid from 'components/core/Grid';

import TaskAltIcon from '@mui/icons-material/TaskAlt';

import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { CheckoutSummaryDamageDepositDialog } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryDamageDepositDialog';
import { CheckoutSummaryDiscountsDialog } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryDiscountsDialog';
import { CheckoutSummaryFeesAndSurchargesDialog } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryFeesAndSurchargesDialog';
import { CheckoutSummaryNightlyRateDialog } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryNightlyRateDialog';
import { CheckoutSummaryOccupancyTaxDialog } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryOccupancyTaxDialog';
import { CheckoutSummaryPriceDetailItem } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryPriceDetailItem';
import {
  accommodationSubtotalBaseRate,
  bookingTotal,
  damageDepositAmount,
  feesAndSurchargesTotal,
  hasLengthOfStayDiscount,
  lengthOfStayDiscount,
  nightlyRatesDisplay,
  taxTotal,
  ownerOfferDiscount,
  alluraOfferDiscount,
  getOwnerGiftCertificateValue,
  getAlluraGiftCertificateValue,
  hasAlluraGiftCertificate,
  hasOwnerGiftCertificate,
} from 'components/views/pages/Checkout/CheckoutSummary/helpers';
import { formatCanadianCurrency } from 'helpers/currencyHelpers';
import { CheckoutStepProps } from 'pages/[propertyId]/reservenow';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { CheckoutSummaryPromotionsDialog } from './CheckoutSummaryPromotionsDialog';
import { SavedAmount } from '../styles';

export const CheckoutSummaryPriceDetails = ({
  checkoutData,
  bookingData,
  isLastStep,
  hasBottomAppDivider = false,
  hasTopAppDivider = true,
}: Pick<CheckoutStepProps, 'checkoutData' | 'bookingData' | 'isLastStep'> & {
  hasBottomAppDivider?: boolean;
  hasTopAppDivider?: boolean;
}) => {
  const { isMd } = useScreenWidth();
  const toggleNightlyRate = useToggle();
  const toggleDiscounts = useToggle();
  const togglePromotions = useToggle();
  const toggleFeesSurcharges = useToggle();
  const toggleOccupancyTax = useToggle();
  const toggleDamageDeposit = useToggle();

  const nightlyDisplay = nightlyRatesDisplay({ checkoutData, bookingData, isLastStep });
  const losDiscount = lengthOfStayDiscount({ checkoutData, bookingData, isLastStep });
  const hasLosDiscount = hasLengthOfStayDiscount({ checkoutData, bookingData, isLastStep });

  const feesAndSurcharges = feesAndSurchargesTotal({ checkoutData, bookingData, isLastStep });

  const occupancyTaxes = taxTotal({ checkoutData, bookingData, isLastStep });

  const subtotalBaseRate = accommodationSubtotalBaseRate({ checkoutData, bookingData, isLastStep });
  const total = bookingTotal({ checkoutData, bookingData, isLastStep });

  const damageDeposit = damageDepositAmount({ checkoutData, bookingData, isLastStep });

  const ownerDiscount = ownerOfferDiscount({ checkoutData, bookingData, isLastStep });
  const alluraDiscount = alluraOfferDiscount({ checkoutData, bookingData, isLastStep });
  const totalSaved = losDiscount + ownerDiscount + alluraDiscount;

  const isLoading = isLastStep ? !bookingData : !checkoutData;

  const isAlluraGiftCertificate = hasAlluraGiftCertificate({ checkoutData, bookingData, isLastStep });
  const isOwnerGiftCertificate = hasOwnerGiftCertificate({ checkoutData, bookingData, isLastStep });
  const owner_gift_certificate_description = getOwnerGiftCertificateValue({ checkoutData, bookingData, isLastStep });
  const allura_gift_certificate_description = getAlluraGiftCertificateValue({ checkoutData, bookingData, isLastStep });

  return (
    <>
      <Grid item xs={12}>
        {hasTopAppDivider ? <AppDivider marginVertical="16px" /> : null}
        <LoadingSkeleton isLoading={isLoading}>
          <Typography variant={isMd ? 'h6' : 'h5'} mb={1}>
            Price Details
          </Typography>
        </LoadingSkeleton>

        <CheckoutSummaryPriceDetailItem
          label={nightlyDisplay}
          value={formatCanadianCurrency(subtotalBaseRate, { minMaxFractionDigits: 2 })}
          isLoading={isLoading}
          onClick={toggleNightlyRate.open}
        />
        {hasLosDiscount ? (
          <CheckoutSummaryPriceDetailItem
            isIndented
            isDiscount
            label="Length of stay discount"
            value={`-${formatCanadianCurrency(losDiscount, { minMaxFractionDigits: 2 })}`}
            isLoading={isLoading}
            onClick={toggleDiscounts.open}
          />
        ) : null}

        {ownerDiscount > 0 && (
          <CheckoutSummaryPriceDetailItem
            isIndented
            isDiscount
            label="Promotions applied"
            value={`-${formatCanadianCurrency(ownerDiscount, { minMaxFractionDigits: 2 })}`}
            isLoading={isLoading}
            onClick={togglePromotions.open}
          />
        )}
        {alluraDiscount > 0 && (
          <CheckoutSummaryPriceDetailItem
            isIndented
            isDiscount
            label="Additional offer applied"
            value={`-${formatCanadianCurrency(alluraDiscount, { minMaxFractionDigits: 2 })}`}
            isLoading={isLoading}
            onClick={togglePromotions.open}
          />
        )}
        {isOwnerGiftCertificate ? (
          <CheckoutSummaryPriceDetailItem
            isIndented
            isDiscount
            label="Gift/Gift Certificate offer applied"
            value={`${owner_gift_certificate_description}`}
            isLoading={isLoading}
            onClick={togglePromotions.open}
          />
        ) : null}
        {isAlluraGiftCertificate ? (
          <CheckoutSummaryPriceDetailItem
            isIndented
            isDiscount
            label="Additional Gift/Gift Certificate offer applied"
            value={`${allura_gift_certificate_description}`}
            isLoading={isLoading}
            onClick={togglePromotions.open}
          />
        ) : null}
        <CheckoutSummaryPriceDetailItem
          label="Fees and surcharges"
          value={formatCanadianCurrency(feesAndSurcharges, { minMaxFractionDigits: 2 })}
          isLoading={isLoading}
          onClick={toggleFeesSurcharges.open}
        />
        <CheckoutSummaryPriceDetailItem
          label="Taxes"
          value={formatCanadianCurrency(occupancyTaxes, { minMaxFractionDigits: 2 })}
          isLoading={isLoading}
          onClick={toggleOccupancyTax.open}
        />
        {totalSaved > 0 ? (
          <>
            <AppDivider marginVertical="16px" />
            <SavedAmount variant="body1"><TaskAltIcon color="success" /> Book today to save {formatCanadianCurrency(totalSaved, { minMaxFractionDigits: 2 })}</SavedAmount>
          </>
        ) : null}
        <AppDivider marginVertical="16px" />
        <CheckoutSummaryPriceDetailItem
          isTotal
          label="Total (CAD)"
          value={formatCanadianCurrency(total, { minMaxFractionDigits: 2 })}
          isLoading={isLoading}
        />
        {damageDeposit ? (
          <CheckoutSummaryPriceDetailItem
            label="Refundable damage deposit"
            value={`+${formatCanadianCurrency(damageDeposit, { minMaxFractionDigits: 2 })}`}
            isLoading={isLoading}
            onClick={toggleDamageDeposit.open}
          />
        ) : null}
        {hasBottomAppDivider ? <AppDivider marginVertical="24px" /> : null}
      </Grid>

      {toggleNightlyRate.isOpen ? (
        <CheckoutSummaryNightlyRateDialog
          isOpen={toggleNightlyRate.isOpen}
          close={toggleNightlyRate.close}
          checkoutData={checkoutData}
          bookingData={bookingData}
          isLastStep={isLastStep}
        />
      ) : null}

      {toggleDiscounts.isOpen ? (
        <CheckoutSummaryDiscountsDialog
          isOpen={toggleDiscounts.isOpen}
          close={toggleDiscounts.close}
          checkoutData={checkoutData}
          bookingData={bookingData}
          isLastStep={isLastStep}
        />
      ) : null}

      {togglePromotions.isOpen ? (
        <CheckoutSummaryPromotionsDialog
          isOpen={togglePromotions.isOpen}
          close={togglePromotions.close}
          checkoutData={checkoutData}
          bookingData={bookingData}
          isLastStep={isLastStep}
        />
      ) : null}

      {toggleFeesSurcharges.isOpen ? (
        <CheckoutSummaryFeesAndSurchargesDialog
          isOpen={toggleFeesSurcharges.isOpen}
          close={toggleFeesSurcharges.close}
          checkoutData={checkoutData}
          bookingData={bookingData}
          isLastStep={isLastStep}
        />
      ) : null}

      {toggleOccupancyTax.isOpen ? (
        <CheckoutSummaryOccupancyTaxDialog
          isOpen={toggleOccupancyTax.isOpen}
          close={toggleOccupancyTax.close}
          checkoutData={checkoutData}
          bookingData={bookingData}
          isLastStep={isLastStep}
        />
      ) : null}

      {toggleDamageDeposit.isOpen ? (
        <CheckoutSummaryDamageDepositDialog
          isOpen={toggleDamageDeposit.isOpen}
          close={toggleDamageDeposit.close}
          checkoutData={checkoutData}
          bookingData={bookingData}
          isLastStep={isLastStep}
        />
      ) : null}
    </>
  );
};
